import React from "react";
import { manualReportKey, readManualReport, manualCardCount } from "../../pages/orders/ordersComponents/manualSavedReport";
import { savedCount } from "../../pages/orders/ordersComponents/automaticCheckState";
import { hasUnresolvedOrderAction as isChecking, canStartManualCheck } from "../../pages/orders/ordersComponents/manualCheckState";
import { useQuery, useQueryClient } from "react-query";
import * as Library from "../../../utils/Library";
import { formatDefaultDateTime, formatPrice } from "../../../utils/Formatter";
import {
    ButtonsEnabledRules, getMappedStatus, orderStatusColors
} from "../../pages/orders/OrdersPage";
import PremiumOrderSummaryCard from "../../pages/orders/PremiumOrderSummaryCard";
import OrderMissingSizesTooltip, {
    isMissingSizeStatus
} from "../../pages/orders/ordersComponents/OrderMissingSizesTooltip";

/**
 * Orders-grid adapter for the accepted v14 presentation. Existing records, formatters,
 * availability rules and callback payloads remain owned by the Orders page.
 * CHECK starts only from the card click; the authenticated manual path remains authoritative.
 */
export default function PremiumOrdersGridCard(props) {
    const data = props?.data;
    const nativeLocked = isChecking(data);
    const queryClient = useQueryClient();
    const companyID = localStorage.getItem("currentCompanyID");
    const storeID = data?.storeID;
    const { data: destinationPOSName } = useQuery(
        ["OrdersDestinationPOS", companyID, storeID],
        () => resolveDestinationPOS(queryClient, companyID, storeID),
        { enabled: !!companyID && typeof storeID === "string" && !!storeID && props?.loading !== true,
            staleTime: 60000, retry: false, refetchOnWindowFocus: false }
    );
    const { data: manualReport, isError: manualReportError } = useQuery(manualReportKey(data), () => readManualReport(data, true), {
        enabled: props.manualReports === true && data?.supplierOrderType === "Bulk Order" && !!data?._id,
        retry: false, staleTime: 0, refetchOnWindowFocus: false
    });
    const locked = nativeLocked || props.manualReports && manualReport?.state === "running";
    const status = data?.status || "--";
    const mappedStatus = getMappedStatus(status);
    const statusColorClass = orderStatusColors.find(item => item.forValues.includes(mappedStatus))?.colorClass;
    const seasons = Array.isArray(data?.seasons) ? data.seasons.join(", ") : "--";
    // Preserve the existing card's missing/zero-total and currency presentation.
    const order = {
        number: data?.orderNumber || undefined,
        botagId: props?.id || "--",
        brand: data?.labelBrandName || "--",
        store: data?.storeName || "--",
        date: formatDefaultDateTime(data?.createdAt),
        source: props?.isCardEnabled ? "Email" : "--",
        season: seasons && props?.isCardEnabled ? seasons : "--",
        totalItems: data?.totalItems || "--",
        formattedTotal: data?.totalPriceIncludingVAT
            ? formatPrice(data.totalPriceIncludingVAT, "kr", "--") : "--",
        status: locked ? "Checking" : mappedStatus,
        itemsToUpdate: props.manualReports ? manualReportError ? "error" : manualCardCount(manualReport) : savedCount(data),
        checkQualification: props.manualReports ? manualReport?.error ? "Last CHECK/save failed; prior report retained" : manualReport?.outdated ? "Outdated — UPDATE required" : manualReport?.qualification : data?.automaticCheck?.state === "partial" ? "Verification incomplete" : null,
        statusColorClass,
        posDisplay: { name: destinationPOSName }
    };
    const invoke = callback => callback ? () => callback({ ...props?.cardData, posDisplay: { name: destinationPOSName } }) : undefined;

    return <PremiumOrderSummaryCard
        className={props?.className}
        order={order}
        locked={locked}
        media={data?.labelBrandImageURL
            ? <img src={data.labelBrandImageURL} alt={data?.labelBrandName || "Label brand"} />
            : <span className="summary-empty-media" aria-hidden="true">--</span>}
        emptyCard={!data}
        loading={props?.loading === true}
        statusAdornment={isMissingSizeStatus(status)
            ? <OrderMissingSizesTooltip data={data} iconSize="14px" placement="left" /> : undefined}
        actions={{
            delete: { onClick: invoke(props?.onDeleteClick) },
            check: { onClick: invoke(props?.onCheckClick), disabled: !canStartManualCheck(data, mappedStatus, props?.loading === true || props?.isCardEnabled === false || locked) },
            rules: { onClick: invoke(props?.onRulesClick), disabled: !ButtonsEnabledRules("rules", status) },
            view: { onClick: invoke(props?.onViewClick) },
            export: { onClick: invoke(props?.onExportClick), disabled: locked || !ButtonsEnabledRules("export", status) }
        }}
    />;
}

// #region DESTINATION POS DISPLAY METADATA
// Display only: the existing authorized store metadata endpoint reads stored data.
// Never infer this name from importedFromPosName or initiate CHECK/provider work.
export async function resolveDestinationPOS(queryClient, companyID, storeID) {
    const readName = rows => {
        const store = rows?.find(item => item?.id === storeID);
        return store ? { found: true, name: store.POSstatus === "Connected" && store.POSsystem !== "--" ? store.POSsystem : null } : null;
    };
    for (const prefix of ["StoresGrid", "StoresList"]) {
        for (const [, cached] of queryClient.getQueriesData([prefix])) {
            const pages = cached?.pages || [cached];
            for (const page of pages) {
                const match = readName(page?.data?.result);
                if (match) return match.name;
            }
        }
    }
    let page = 1;
    while (true) {
        // Page cache is shared by cards for different destination stores, avoiding
        // repeated page reads while retaining company and pagination isolation.
        const result = await queryClient.fetchQuery(["OrdersStoreMetadata", companyID, page], async () => {
            const response = await new Promise((resolve, reject) => {
                Library.makeApiRequest({ endpoint: "Stores/getStoresData",
                    data: { companyID, page, limit: 50 }, onErrorShowMsg: false,
                    onError: () => reject(new Error("Destination POS metadata unavailable"))
                }).then(resolve, reject);
            });
            if (response?.data?.status !== 200 || !Array.isArray(response?.data?.data?.result))
                throw new Error("Destination POS metadata unavailable");
            return response.data.data;
        }, { staleTime: 60000, retry: false });
        const match = readName(result.result);
        if (match) return match.name;
        if (result.nextPage == null) return null;
        if (!Number.isInteger(result.nextPage) || result.nextPage !== page + 1)
            throw new Error("Invalid store metadata pagination");
        page = result.nextPage;
    }
}
// #endregion
